import { Component, input, model, computed, ChangeDetectionStrategy, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonGroupComponent, ButtonGroupOption } from '../button-group/button-group.component';
import { ZagButton } from '../button/button';
import { FieldsetComponent } from '../fieldset/fieldset';
import { ZagIcon } from '../icon/icon';
import {
  CollectionViewMode,
  CollectionSortOptionBase,
  CollectionSortDirection,
  CollectionFilterGroup,
  CollectionFilterValues,
  type CollectionFilterPrimitive,
} from './types';

export interface TranslatedSortOption {
  id: string;
  label: string;
  icon?: string;
}

interface TranslatedFilterGroup {
  id: string;
  label: string;
  clearLabel?: string;
  allowMultiple: boolean;
  options: Array<{
    value: CollectionFilterPrimitive;
    label: string;
    icon?: string;
  }>;
}

@Component({
  selector: 'zag-collection-layout-controls',
  imports: [TranslatePipe, ButtonGroupComponent, ZagButton, FieldsetComponent, ZagIcon],
  templateUrl: './collection-layout-controls.component.html',
  styleUrls: ['./collection-layout-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionLayoutControlsComponent {
  private readonly translate = inject(TranslateService);

  /** Subscribe to language changes to reactively update labels */
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang ?? 'en', translations: {} },
  });

  /** Available view mode options */
  viewModes = input<CollectionViewMode[]>();

  /** Available sort options */
  sortOptions = input<CollectionSortOptionBase[]>();

  /** Current view mode (two-way binding) */
  viewMode = model<string>('card');

  /** Current sort option (two-way binding) */
  sortBy = model<string | undefined>(undefined);

  /** Current sort direction (two-way binding) */
  sortDirection = model<CollectionSortDirection>('asc');

  /** Available grouped filter options */
  filterGroups = input<CollectionFilterGroup[] | undefined>(undefined);

  /** Currently selected filter values keyed by group id */
  filterValues = model<CollectionFilterValues>({});

  /** Whether to show view mode controls */
  protected readonly showViewModes = computed(() => (this.viewModes()?.length ?? 0) > 0);

  /** Whether to show sort controls */
  protected readonly showSort = computed(() => (this.sortOptions()?.length ?? 0) > 0);

  /** Whether to show grouped filters */
  protected readonly showFilter = computed(() => (this.filterGroups()?.length ?? 0) > 0);

  /** View mode options with translated labels */
  protected readonly viewModeButtonOptions = computed<ButtonGroupOption[]>(() => {
    this.currentLang();
    const modes = this.viewModes();
    if (!modes) return [];
    return modes.map(mode => ({
      value: mode.id,
      label: this.translate.instant(mode.label),
      icon: mode.icon,
    }));
  });

  /** Sort options with translated labels */
  protected readonly translatedSortOptions = computed<TranslatedSortOption[]>(() => {
    this.currentLang();
    const options = this.sortOptions();
    if (!options) return [];
    return options.map(opt => ({
      id: opt.id,
      label: this.translate.instant(opt.label),
      icon: opt.icon,
    }));
  });

  /** Icon for the current sort direction toggle */
  protected readonly sortDirectionIcon = computed(() => (this.sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward'));

  /** Toggle sort direction between asc and desc */
  protected toggleSortDirection(event: Event): void {
    event.stopPropagation();
    this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
  }

  /** Select a sort option */
  protected selectSort(id: string): void {
    this.sortBy.set(id);
  }

  /** Translated filter groups */
  protected readonly translatedFilterGroups = computed<TranslatedFilterGroup[]>(() => {
    this.currentLang();
    const groups = this.filterGroups();
    if (!groups) return [];
    return groups.map(group => ({
      id: group.id,
      label: this.translate.instant(group.label),
      clearLabel: group.clearLabel ? this.translate.instant(group.clearLabel) : undefined,
      allowMultiple: group.allowMultiple ?? false,
      options: group.options.map(option => ({
        value: option.value,
        label: this.translate.instant(option.label),
        icon: option.icon,
      })),
    }));
  });

  protected isFilterSelected(groupId: string, value: CollectionFilterPrimitive): boolean {
    const selectedValues = this.filterValues()[groupId] ?? [];
    return selectedValues.some(selectedValue => selectedValue === value);
  }

  protected hasSelectedFilters(groupId: string): boolean {
    return (this.filterValues()[groupId]?.length ?? 0) > 0;
  }

  protected toggleFilter(groupId: string, value: CollectionFilterPrimitive, allowMultiple: boolean): void {
    this.filterValues.update(current => {
      const selectedValues = current[groupId] ?? [];
      const alreadySelected = selectedValues.some(selectedValue => selectedValue === value);

      if (!allowMultiple) {
        return { ...current, [groupId]: alreadySelected ? [] : [value] };
      }

      const nextValues = alreadySelected ? selectedValues.filter(selectedValue => selectedValue !== value) : [...selectedValues, value];

      return { ...current, [groupId]: nextValues };
    });
  }

  protected clearFilterGroup(event: Event, groupId: string): void {
    event.stopPropagation();
    this.filterValues.update(current => ({ ...current, [groupId]: [] }));
  }
}
